import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { 
  Loader2, 
  Settings2,
  Edit,
  AlertCircle,
  ArrowRight,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProgramServiceMappingsPage = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      const response = await axios.get(`${API}/admin/program-service-mappings`);
      setMappings(response.data);
    } catch (error) {
      toast.error('Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mapping) => {
    setEditingMapping(mapping);
    setSelectedServices(mapping.allowed_service_types || []);
    setShowDialog(true);
  };

  const handleToggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSubmit = async () => {
    if (selectedServices.length === 0) {
      toast.error('At least one service type must be selected');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/program-service-mappings/${editingMapping.program_type_id}`, {
        allowed_service_types: selectedServices
      });
      toast.success('Mapping updated successfully');
      setShowDialog(false);
      fetchMappings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mapping');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="program-service-mappings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Settings2 className="w-8 h-8 text-primary" />
          Program-Service Mappings
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure which service types are allowed for each program type
        </p>
      </div>

      {/* Info Alert */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-100">Validation Enforcement</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                These mappings control which service types can be selected when creating cases.
                The system will prevent mismatched program/service combinations.
                Changes take effect immediately but do not affect historical cases.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mappings List */}
      <div className="space-y-4">
        {mappings.map((mapping) => (
          <Card key={mapping.program_type_id} className="card-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {mapping.program_type_name}
                    {mapping.is_active !== false && (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {mapping.description}
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleOpenDialog(mapping)}>
                  <Edit className="w-4 h-4 mr-1" />
                  Edit Mapping
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-medium">Allowed Service Types:</span>
                  <span>{mapping.allowed_service_types?.length || 0} configured</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {mapping.allowed_service_types_details?.map((st) => (
                    <Badge 
                      key={st.id} 
                      variant={st.is_active ? "default" : "secondary"}
                      className={st.is_active ? "bg-primary/10 text-primary border border-primary/20" : ""}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      {st.name}
                    </Badge>
                  ))}
                  {(!mapping.allowed_service_types_details || mapping.allowed_service_types_details.length === 0) && (
                    <span className="text-sm text-muted-foreground italic">No service types configured</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Service Type Mapping</DialogTitle>
            <DialogDescription>
              Select which service types are allowed for <strong>{editingMapping?.program_type_name}</strong>.
              At least one service type must be selected.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-muted/50">
              <span className="font-medium">{editingMapping?.program_type_name}</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Allowed Service Types</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {editingMapping?.available_service_types?.map((st) => (
                <div
                  key={st.id}
                  onClick={() => handleToggleService(st.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedServices.includes(st.id)
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  } ${!st.is_active ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedServices.includes(st.id)}
                      onCheckedChange={() => handleToggleService(st.id)}
                    />
                    <div>
                      <p className="font-medium">{st.name}</p>
                      {!st.is_active && (
                        <p className="text-xs text-muted-foreground">Inactive</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 rounded-lg border border-dashed">
              <p className="text-sm text-muted-foreground">
                <strong>{selectedServices.length}</strong> service type(s) selected
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={saving || selectedServices.length === 0}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramServiceMappingsPage;
